import { useEffect } from 'react'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import { toast } from '@/shared/lib/toast'
import socketService from '@/shared/socket/socket.service'
import { useNotificationStore } from '@/shared/stores/notification.store'

import { useSalesLeadsStore } from '../stores/salesLeads.store'

export const useSalesLeadNotifications = () => {
  const role = useAuthStore((state) => state.user?.role)
  const userId = useAuthStore((state) => state.user?.id || state.user?._id)

  useEffect(() => {
    if (role !== 'sales' || !userId) {
      return undefined
    }

    // Fetch persisted notifications on mount
    void useNotificationStore.getState().fetchNotifications?.()

    const socket = socketService.connect()
    if (!socket) return undefined

    const handleLeadAssigned = (data) => {
      const title = data.title || 'New Lead Assigned'
      const body =
        data.body ||
        (data.leadName
          ? `Lead "${data.leadName}" has been assigned to you.`
          : 'A new lead has been assigned to you.')

      toast.info(body)

      useNotificationStore.getState().addNotification({
        id: data.id || `lead-assigned-${data.leadId}-${Date.now()}`,
        title,
        body,
        type: 'lead_assigned',
        data: {
          leadId: data.leadId,
        },
        read: false,
        createdAt: data.createdAt || new Date().toISOString(),
      })

      // Refresh sales leads table if salesLeadsStore is active
      try {
        useSalesLeadsStore.getState().fetchLeads?.()
      } catch {
        // Ignore if store not yet initialized
      }
    }

    const handleFollowUpRequired = (data) => {
      const title = data.title || 'Follow-up Required'
      const body =
        data.body ||
        (data.leadName
          ? `Admin requested a follow-up for lead "${data.leadName}".`
          : 'Follow-up requested for a lead.')

      toast.info(body)

      useNotificationStore.getState().addNotification({
        id: data.id || `follow-up-${data.leadId}-${Date.now()}`,
        title,
        body,
        type: 'follow_up_required',
        data: {
          leadId: data.leadId,
        },
        read: false,
        createdAt: data.createdAt || new Date().toISOString(),
      })

      try {
        useSalesLeadsStore.getState().fetchLeads?.()
      } catch {
        // Ignore
      }
    }

    const handleNotificationNew = (data) => {
      if (!data) return
      useNotificationStore.getState().addNotification(data)

      if (
        data.type === 'lead_assigned' ||
        data.type === 'follow_up_required' ||
        data.type === 'message'
      ) {
        toast.info(data.body || data.title)
        try {
          useSalesLeadsStore.getState().fetchLeads?.()
        } catch {
          // Ignore
        }
      }
    }

    socket.on('lead:assigned', handleLeadAssigned)
    socket.on('lead:follow-up-required', handleFollowUpRequired)
    socket.on('notification:new', handleNotificationNew)

    return () => {
      socket.off('lead:assigned', handleLeadAssigned)
      socket.off('lead:follow-up-required', handleFollowUpRequired)
      socket.off('notification:new', handleNotificationNew)
    }
  }, [role, userId])
}
